/* Forstwald, product.js: Produktseite.
   <fw-product-gallery>  Wischreihe mit Zähler, Pfeile, Miniaturen (mobil und Desktop), Lightbox mit Zoom
   <fw-product-info>     Variantenwahl, Preis, Kaufknopf, Sticky-Leiste, Größenhilfe, Benachrichtigung, Geschenkverpackung
   Geschenkverpackung: Ist das Kästchen gewählt, geht das Hauptprodukt zusammen mit der Verpackung (eigenes Produkt) per
   cart/add.js mit items-Array in den Warenkorb. Die Verpackungszeile trägt die Eigenschaften Kartentext (sichtbar) und
   _fuer (unsichtbar, ID der Variante des Hauptprodukts), damit der Warenkorb sie dem Teil zuordnen kann.
   Ohne JavaScript bleibt das Formular ein normales Formular (Auswahlfeld in noscript, sendet an /cart/add). */
(() => {
  'use strict';
  const FW = window.Forstwald;
  if (!FW) return;
  const d = document;
  const root = d.documentElement;
  const wide = matchMedia('(min-width: 900px)');
  const fill = (tpl, map) => (tpl || '').replace(/%(\w)/g, (m, k) => (map[k] !== undefined ? map[k] : m));
  const json = (el) => {
    try {
      return el ? JSON.parse(el.textContent) : null;
    } catch (e) {
      return null;
    }
  };
  /* --header-offset kommt als px (section-header.js) oder vor dem ersten Lauf als rem (base.css): beide Einheiten abfangen */
  const headerPx = () => {
    const cs = getComputedStyle(root);
    const raw = cs.getPropertyValue('--header-offset').trim();
    const n = parseFloat(raw);
    if (!n) return 72;
    return /rem$/.test(raw) ? n * (parseFloat(cs.fontSize) || 16) : n;
  };
  const behavior = () => (FW.reduceMotion && FW.reduceMotion() ? 'auto' : 'smooth');

  /* ------------------------------------------------------------------------
     Galerie
     ------------------------------------------------------------------------ */
  class FwProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track || this.ready) return;
      this.ready = true;
      this.slides = [...this.track.querySelectorAll('.gallery__slide')];
      this.total = this.slides.length;
      this.index = 0;
      this.counters = [...this.querySelectorAll('[data-gallery-counter]')];
      this.prevBtn = this.querySelector('[data-gallery-prev]');
      this.nextBtn = this.querySelector('[data-gallery-next]');
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.tpl = this.dataset.counterTemplate || '%c / %t';

      if (this.total > 1) {
        let queued = false;
        this.track.addEventListener(
          'scroll',
          () => {
            if (queued) return;
            queued = true;
            requestAnimationFrame(() => {
              queued = false;
              this.onScroll();
            });
          },
          { passive: true }
        );
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));
        this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(+t.dataset.galleryThumb)));
        this.track.addEventListener('keydown', (e) => {
          if (!this.isScroller() || e.target !== this.track) return;
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.step(1);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.step(-1);
          }
        });
        this.setIndex(0);
      }
      this.initVideos();
      if (this.dataset.zoom === 'lightbox') this.initLightbox();
    }

    /* Kurze Videos (Drehteller): stumm, in Schleife, nur im Blick abspielen. Bei reduzierter Bewegung, Datensparen oder
       ohne IntersectionObserver bleibt es beim Vorschaubild, das Video startet dann über die Bedienelemente.
       Wer selbst pausiert, wird nicht wieder gestartet, bis er selbst abspielt. */
    initVideos() {
      const videos = [...this.querySelectorAll('video.gallery__video')];
      if (!videos.length) return;
      videos.forEach((v) => {
        v.muted = true;
        v.loop = true;
        v.playsInline = true;
      });
      const saver = navigator.connection && navigator.connection.saveData;
      const calm = FW.reduceMotion && FW.reduceMotion();
      if (calm || saver || !('IntersectionObserver' in window)) return;
      /* Eigene Aufrufe werden gezählt, nicht als Flag geführt: Kommen play() und pause() im selben Task, bleibt die
         Zuordnung der Ereignisse eindeutig und eine Pause durch das Skript gilt nie als Pause der Person. */
      const expect = new WeakMap();
      const own = (v) => {
        if (!expect.has(v)) expect.set(v, { play: 0, pause: 0 });
        return expect.get(v);
      };
      const run = (v, on) => {
        const n = own(v);
        if (on && v.paused) {
          n.play += 1;
          const p = v.play();
          if (p && p.catch) {
            p.catch(() => {
              n.play = Math.max(0, n.play - 1);
            });
          }
        } else if (!on && !v.paused) {
          n.pause += 1;
          v.pause();
        }
      };
      /* Abspielen erst nach kurzer Ruhe im Blick: Wer nur durch die Reihe springt, lädt das Video nicht */
      const timers = new WeakMap();
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            const v = e.target;
            clearTimeout(timers.get(v));
            if (e.isIntersecting && e.intersectionRatio >= 0.6) {
              if (!v.dataset.userPaused) timers.set(v, setTimeout(() => run(v, true), 250));
            } else {
              run(v, false);
            }
          });
        },
        { threshold: [0, 0.6] }
      );
      videos.forEach((v) => {
        v.addEventListener('pause', () => {
          const n = own(v);
          if (n.pause > 0) n.pause -= 1;
          else v.dataset.userPaused = '1';
        });
        v.addEventListener('play', () => {
          const n = own(v);
          if (n.play > 0) n.play -= 1;
          else delete v.dataset.userPaused;
        });
        io.observe(v);
      });
    }

    isScroller() {
      return this.track.scrollWidth > this.track.clientWidth + 2 && getComputedStyle(this.track).overflowX !== 'visible';
    }

    onScroll() {
      if (!this.isScroller()) return;
      const t = this.track;
      const left = t.getBoundingClientRect().left + (parseFloat(getComputedStyle(t).paddingLeft) || 0);
      let best = 0;
      let dist = Infinity;
      this.slides.forEach((s, i) => {
        const dx = Math.abs(s.getBoundingClientRect().left - left);
        if (dx < dist) {
          dist = dx;
          best = i;
        }
      });
      if (t.scrollLeft >= t.scrollWidth - t.clientWidth - 2) best = this.total - 1;
      this.setIndex(best);
    }

    setIndex(i) {
      this.index = i;
      this.slides.forEach((s, n) => s.classList.toggle('is-current', n === i));
      const text = fill(this.tpl, { c: i + 1, t: this.total });
      this.counters.forEach((c) => (c.textContent = text));
      this.thumbs.forEach((t, n) => {
        t.classList.toggle('is-current', n === i);
        if (n === i) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      /* Miniaturleiste (mobil eine Reihe zum Wischen): die aktuelle Miniatur bleibt im Blick, ohne die Seite zu bewegen */
      const strip = this.thumbs[i] && this.thumbs[i].closest('.gallery__thumbs');
      if (strip && strip.scrollWidth > strip.clientWidth + 2) {
        const t = this.thumbs[i];
        const left = t.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
        strip.scrollTo({ left: left - (strip.clientWidth - t.offsetWidth) / 2, behavior: 'auto' });
      }
      if (this.prevBtn) this.prevBtn.disabled = i <= 0;
      if (this.nextBtn) this.nextBtn.disabled = i >= this.total - 1;
    }

    step(delta) {
      this.goTo(Math.min(this.total - 1, Math.max(0, this.index + delta)));
    }

    goTo(i, mode) {
      const slide = this.slides[i];
      if (!slide) return;
      const how = mode || behavior();
      if (this.isScroller()) {
        const t = this.track;
        const pad = parseFloat(getComputedStyle(t).paddingLeft) || 0;
        const dx = slide.getBoundingClientRect().left - t.getBoundingClientRect().left - pad;
        t.scrollTo({ left: t.scrollLeft + dx, behavior: how });
      } else {
        const y = slide.getBoundingClientRect().top + window.scrollY - headerPx() - 16;
        window.scrollTo({ top: Math.max(0, y), behavior: how });
      }
      this.setIndex(i);
    }

    showMedia(id) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i < 0) return;
      /* Ist das Bild schon sichtbar, bleibt die Seite ruhig */
      if (!this.isScroller()) {
        const r = this.slides[i].getBoundingClientRect();
        if (r.top >= headerPx() && r.bottom <= innerHeight) {
          this.setIndex(i);
          return;
        }
      }
      this.goTo(i);
    }

    /* Lightbox */
    initLightbox() {
      const lb = this.querySelector('[data-gallery-lightbox]');
      if (!lb) return;
      this.lb = lb;
      this.lbImg = lb.querySelector('[data-lightbox-img]');
      this.lbStage = lb.querySelector('[data-lightbox-stage]');
      this.lbCounter = lb.querySelector('[data-lightbox-counter]');
      this.lbZoom = lb.querySelector('[data-lightbox-zoom]');
      this.zoomable = this.slides.map((s, i) => (s.dataset.zoomSrc ? i : -1)).filter((i) => i >= 0);
      this.pos = 0;

      this.addEventListener('click', (e) => {
        const a = e.target.closest('[data-gallery-zoom]');
        if (!a || e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        this.openLightbox(+a.dataset.galleryZoom, a);
      });
      const prev = lb.querySelector('[data-lightbox-prev]');
      const next = lb.querySelector('[data-lightbox-next]');
      if (prev) prev.addEventListener('click', () => this.lbStep(-1));
      if (next) next.addEventListener('click', () => this.lbStep(1));
      if (this.lbZoom) this.lbZoom.addEventListener('click', () => this.setZoom(!this.zoomed));
      lb.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.lbStep(1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.lbStep(-1);
        }
      });
      lb.addEventListener('close', () => {
        this.setZoom(false);
        const idx = this.zoomable[this.pos];
        if (idx !== undefined && idx !== this.index) this.goTo(idx, 'auto');
      });

      /* Wischen wechselt das Bild, Klick zoomt, Ziehen verschiebt den Ausschnitt (Maus) */
      const stage = this.lbStage;
      let drag = null;
      stage.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        drag = { x: e.clientX, y: e.clientY, sl: stage.scrollLeft, st: stage.scrollTop, moved: false };
      });
      stage.addEventListener('pointermove', (e) => {
        if (!drag) return;
        const dx = e.clientX - drag.x;
        const dy = e.clientY - drag.y;
        if (Math.abs(dx) > 5 || Math.abs(dy) > 5) drag.moved = true;
        if (this.zoomed && e.pointerType === 'mouse' && drag.moved) {
          if (!stage.hasPointerCapture(e.pointerId)) stage.setPointerCapture(e.pointerId);
          stage.classList.add('is-dragging');
          stage.scrollLeft = drag.sl - dx;
          stage.scrollTop = drag.st - dy;
        }
      });
      const end = (e) => {
        if (!drag) return;
        const dx = e.clientX - drag.x;
        const dy = e.clientY - drag.y;
        const moved = drag.moved;
        drag = null;
        stage.classList.remove('is-dragging');
        if (e.type !== 'pointerup') return;
        if (!this.zoomed && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.lbStep(dx < 0 ? 1 : -1);
          return;
        }
        if (!moved && e.target === this.lbImg) this.setZoom(!this.zoomed);
      };
      stage.addEventListener('pointerup', end);
      stage.addEventListener('pointercancel', end);
    }

    openLightbox(slideIndex, trigger) {
      const p = this.zoomable.indexOf(slideIndex);
      this.showLightbox(p < 0 ? 0 : p);
      FW.openDialog(this.lb, trigger);
    }

    showLightbox(p) {
      const n = this.zoomable.length;
      if (!n) return;
      this.pos = (p + n) % n;
      const slide = this.slides[this.zoomable[this.pos]];
      this.setZoom(false);
      this.lbImg.src = slide.dataset.zoomSrc;
      this.lbImg.alt = slide.dataset.zoomAlt || '';
      if (slide.dataset.zoomWidth) this.lbImg.width = +slide.dataset.zoomWidth;
      if (slide.dataset.zoomHeight) this.lbImg.height = +slide.dataset.zoomHeight;
      /* Zählt wie die Galerie alle Medien, damit beide Zähler übereinstimmen, auch wenn ein Video dazwischenliegt */
      if (this.lbCounter) this.lbCounter.textContent = n > 1 ? fill(this.tpl, { c: this.zoomable[this.pos] + 1, t: this.total }) : '';
      [1, -1].forEach((dir) => {
        const nb = this.slides[this.zoomable[(this.pos + dir + n) % n]];
        if (nb && nb !== slide) new Image().src = nb.dataset.zoomSrc;
      });
    }

    lbStep(delta) {
      if (this.zoomable.length > 1) this.showLightbox(this.pos + delta);
    }

    setZoom(on) {
      if (!this.lb) return;
      this.zoomed = on;
      this.lb.classList.toggle('is-zoomed', on);
      if (this.lbZoom) this.lbZoom.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) {
        requestAnimationFrame(() => {
          const s = this.lbStage;
          s.scrollLeft = (s.scrollWidth - s.clientWidth) / 2;
          s.scrollTop = (s.scrollHeight - s.clientHeight) / 2;
        });
      } else if (this.lbStage) {
        this.lbStage.scrollLeft = 0;
        this.lbStage.scrollTop = 0;
      }
    }
  }
  if (!customElements.get('fw-product-gallery')) customElements.define('fw-product-gallery', FwProductGallery);

  /* ------------------------------------------------------------------------
     Produktinfo: Varianten, Kaufknopf, Sticky, Sheet, Benachrichtigung, Geschenk
     ------------------------------------------------------------------------ */
  class FwProductInfo extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.section = this.closest('[data-product-section]') || d;
      this.layout = this.closest('.product__layout');
      this.form = this.querySelector('[data-product-form]');
      this.variants = json(this.querySelector('[data-product-variants]')) || [];
      this.s = json(this.querySelector('[data-product-strings]')) || {};
      this.groups = [...this.querySelectorAll('[data-option-group]')];
      this.optionCount = this.groups.length;
      this.btn = this.form && this.form.querySelector('[data-add-to-cart]');
      this.idInput = this.form && this.form.querySelector('[data-variant-id]');
      this.errorEl = this.form && this.form.querySelector('[data-product-error]');
      this.priceEl = this.querySelector('[data-product-price]');
      this.sticky = this.querySelector('[data-sticky-atc]');
      this.stickyBtn = this.querySelector('[data-sticky-submit]');
      this.sheet = this.querySelector('[data-size-sheet]');
      this.notify = this.querySelector('[data-notify]');
      this.giftCard = this.dataset.giftCard === 'true';
      this.title = this.dataset.productTitle || '';
      this.url = this.dataset.productUrl || location.pathname;

      const current = this.idInput && !this.idInput.disabled ? +this.idInput.value : 0;
      this.variant = this.variants.find((v) => v.id === current) || null;
      this.onlyDefault = this.optionCount === 0 && this.variants.length === 1;
      if (this.onlyDefault) this.variant = this.variants[0];

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-value]')) this.onOption(e.target);
      });
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.add(this.btn);
        });
      }
      if (this.stickyBtn) this.stickyBtn.addEventListener('click', () => this.add(this.stickyBtn));
      this.initSheet();
      this.initNotify();
      this.initGift();
      this.initGiftCard();
      this.initSticky();
      this.initStickyInfo();
      this.refresh(false);
    }

    disconnectedCallback() {
      (this.cleanup || []).forEach((f) => f());
    }

    /* Auswahl lesen und Variante finden */
    selected() {
      const out = new Array(this.optionCount).fill(null);
      this.groups.forEach((g) => {
        const c = g.querySelector('[data-option-value]:checked');
        out[+g.dataset.optionIndex] = c ? c.value : null;
      });
      return out;
    }

    find(sel) {
      return this.variants.find((v) => sel.every((val, i) => v.options[i] === val)) || null;
    }

    onOption(input) {
      const group = input.closest('[data-option-group]');
      const gi = +group.dataset.optionIndex;
      const sel = this.selected();
      const matches = this.variants.filter((v) => sel.every((val, i) => val === null || v.options[i] === val));
      let adjusted = false;
      if (!matches.length) {
        /* Kombination gibt es nicht: nächstliegende Variante mit dem neuen Wert nehmen */
        const pool = this.variants.filter((v) => v.options[gi] === input.value);
        const score = (v) => sel.reduce((n, val, i) => n + (i !== gi && val !== null && v.options[i] === val ? 1 : 0), 0) * 2 + (v.available ? 1 : 0);
        pool.sort((a, b) => score(b) - score(a));
        const pick = pool[0];
        if (pick) {
          this.groups.forEach((g) => {
            const i = +g.dataset.optionIndex;
            if (i === gi || sel[i] === null) return;
            const r = [...g.querySelectorAll('[data-option-value]')].find((x) => x.value === pick.options[i]);
            if (r) r.checked = true;
          });
          adjusted = true;
        }
      }
      this.refresh(true, adjusted);
    }

    refresh(user, adjusted) {
      const sel = this.selected();
      const complete = this.optionCount === 0 || sel.every((v) => v !== null);
      const prev = this.variant;
      this.variant = this.onlyDefault ? this.variants[0] : complete ? this.find(sel) : null;
      const v = this.variant;

      this.markOptions(sel);
      this.renderPrice(v);
      this.renderButtons(v, sel);
      this.renderNotify(v);
      this.renderSheet(sel);
      this.renderSizeGuide(sel);
      if (this.idInput) {
        this.idInput.disabled = !v;
        this.idInput.value = v ? v.id : '';
      }
      if (this.dataset.scrollMedia === 'true' && user && v && v.mediaId && (!prev || prev.mediaId !== v.mediaId)) {
        const g = this.section.querySelector('fw-product-gallery');
        if (g && g.showMedia) g.showMedia(v.mediaId);
      }
      if (user) {
        this.clearError();
        if (v && !this.onlyDefault) {
          const url = new URL(location.href);
          url.searchParams.set('variant', v.id);
          try {
            history.replaceState(null, '', url.toString());
          } catch (e) {}
        }
        let msg = '';
        if (adjusted) msg = this.s.combinationMissing + ' ';
        if (v) msg += fill(v.available ? this.s.variantStatusAvailable : this.s.variantStatusSoldOut, { v: v.label || v.title, p: v.priceText });
        if (msg) FW.announce(msg.trim());
        FW.emit('variant:change', { variant: v, section: this.section });
      }
    }

    /* Werte ohne passende Variante, ausverkaufte Werte, gewählter Name */
    markOptions(sel) {
      const mode = (this.querySelector('[data-variant-picker]') || {}).dataset;
      const disableSoldout = mode && mode.soldoutMode === 'disable';
      this.groups.forEach((g) => {
        const gi = +g.dataset.optionIndex;
        g.querySelectorAll('.option__value').forEach((wrap) => {
          const input = wrap.querySelector('[data-option-value]');
          const label = wrap.querySelector('.option__btn');
          const hint = wrap.querySelector('[data-soldout-text]');
          const pool = this.variants.filter((v) => v.options[gi] === input.value && sel.every((s, i) => i === gi || s === null || v.options[i] === s));
          const exists = pool.length > 0;
          const avail = pool.some((v) => v.available);
          if (label) {
            label.classList.toggle('is-unavailable', !exists);
            label.classList.toggle('is-soldout', exists && !avail);
          }
          if (hint) hint.hidden = !(exists && !avail);
          input.disabled = !!disableSoldout && exists && !avail && !input.checked;
        });
        const out = g.querySelector('[data-option-selected]');
        if (out) out.textContent = sel[gi] || '';
        g.classList.remove('is-attention');
      });
    }

    renderPrice(v) {
      const el = this.priceEl;
      const amount = el && el.querySelector('.price__amount');
      if (!amount) return;
      if (v) amount.textContent = v.priceText;
      else if (el.dataset.varies === 'true') amount.textContent = fill(el.dataset.fromTemplate, { p: el.dataset.minPrice });
      else amount.textContent = el.dataset.minPrice;
    }

    missingLabel(sel) {
      const missing = this.groups.filter((g) => sel[+g.dataset.optionIndex] === null);
      if (missing.some((g) => g.dataset.optionKind === 'size')) return this.s.chooseSize;
      if (missing.some((g) => g.dataset.optionKind === 'color')) return this.s.chooseColor;
      return this.s.chooseOption;
    }

    renderButtons(v, sel) {
      let state = 'add';
      let label = this.s.add;
      if (!v) {
        state = 'select';
        label = this.missingLabel(sel);
      } else if (!v.available) {
        state = 'soldout';
        label = this.s.soldOut;
      }
      this.state = state;
      this.label = label;
      [
        [this.btn, '[data-add-label]'],
        [this.stickyBtn, '[data-sticky-label]'],
      ].forEach(([b, selector]) => {
        if (!b) return;
        b.dataset.state = state;
        const l = b.querySelector(selector);
        if (l && !this.adding && !b.classList.contains('is-done')) l.textContent = label;
        if (state === 'soldout') b.setAttribute('aria-disabled', 'true');
        else if (!this.adding) b.removeAttribute('aria-disabled');
      });
      /* Geschenkverpackung nur anbieten, solange sich die gewählte Variante kaufen lässt */
      const wrapEl = this.querySelector('[data-gift-wrap]');
      if (wrapEl) {
        wrapEl.hidden = state === 'soldout';
        if (state === 'soldout') {
          const box = wrapEl.querySelector('[data-gift-wrap-box]');
          const note = wrapEl.querySelector('[data-gift-wrap-note]');
          if (box && box.checked) box.checked = false;
          if (note) note.hidden = true;
        }
      }
      const meta = this.querySelector('[data-sticky-meta]');
      if (meta) {
        const price = v ? v.priceText : (this.priceEl && this.priceEl.querySelector('.price__amount') ? this.priceEl.querySelector('.price__amount').textContent : '');
        /* Gutscheinwerte: Bezeichnung und Preis sind derselbe Text, dann nur einmal zeigen */
        const same = v && v.label && String(v.label).replace(/\s+/g, ' ').trim() === String(price).replace(/\s+/g, ' ').trim();
        const tax = meta.dataset.tax ? ' ' + meta.dataset.tax : '';
        meta.textContent = (v && v.label && !same ? v.label + ' · ' + price : price) + tax;
      }
    }

    renderNotify(v) {
      const n = this.notify;
      if (!n) return;
      const pinned = n.querySelector('[data-notify-success]');
      const soldAll = this.variants.length > 0 && this.variants.every((x) => !x.available);
      n.hidden = pinned ? false : v ? v.available : !soldAll;
      const vt = v ? v.label || '' : '';
      const set = (sel, val) => {
        const el = n.querySelector(sel);
        if (el) el.value = val;
      };
      const link = location.origin + this.url + (v && !this.onlyDefault ? '?variant=' + v.id : '');
      set('[data-notify-variant]', vt);
      set('[data-notify-url]', link);
      set('[data-notify-body]', fill(this.s.notifyBody, { n: this.title, v: vt, u: link }).replace(/\s{2,}/g, ' '));
      this.notifyName = vt ? this.title + ', ' + vt : this.title;
    }

    renderSheet(sel) {
      if (!this.sheet) return;
      const si = +this.dataset.sizeIndex;
      this.sheet.querySelectorAll('[data-sheet-size]').forEach((b) => {
        const val = b.dataset.sheetSize;
        const pool = this.variants.filter((v) => v.options[si] === val && sel.every((s, i) => i === si || s === null || v.options[i] === s));
        const out = pool.length > 0 && !pool.some((v) => v.available);
        b.classList.toggle('is-soldout', out);
        b.classList.toggle('is-selected', sel[si] === val);
        b.setAttribute('aria-pressed', sel[si] === val ? 'true' : 'false');
        const hint = b.querySelector('[data-soldout-text]');
        if (hint) hint.hidden = !out;
      });
      /* Hinweiszeile im Blatt: vor der Wahl die Aufforderung, danach die gewählte Größe */
      const note = this.sheet.querySelector('[data-size-sheet-hint]');
      if (note) note.textContent = sel[si] ? fill(this.s.sizeChosen, { s: sel[si] }) : this.s.chooseSizeFirst;
    }

    /* Maßtabelle: Zeile der gewählten Größe markieren (Akkordeon und Größenhilfe) */
    renderSizeGuide(sel) {
      const si = +this.dataset.sizeIndex;
      const val = si >= 0 ? sel[si] : null;
      this.querySelectorAll('.size-table__table tr[data-size]').forEach((tr) => tr.classList.toggle('is-selected', !!val && tr.dataset.size === val));
    }

    /* Größenhilfe als Blatt. Öffnet der Kaufknopf es (mobil, Größe fehlt), schließt die Größenwahl das Blatt wieder.
       Öffnet es der Link Größenhilfe, bleibt es offen, damit man Wahl und Tabelle zusammen sieht. */
    initSheet() {
      if (!this.sheet) return;
      this.sheet.addEventListener('click', (e) => {
        const b = e.target.closest('[data-sheet-size]');
        if (!b) return;
        const group = this.groups.find((g) => g.dataset.optionKind === 'size');
        const input = group && [...group.querySelectorAll('[data-option-value]')].find((x) => x.value === b.dataset.sheetSize);
        if (!input) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        if (this.sheet.dataset.mode !== 'choose') {
          FW.announce(fill(this.s.sizeChosen, { s: b.dataset.sheetSize }));
          return;
        }
        /* Nach dem Schließen: Fokus auf den Kaufknopf und die Wahl ansagen. Es wird nichts ohne zweiten Tipp gekauft. */
        this.sheet.addEventListener(
          'close',
          () => {
            const target = this.lastTrigger && this.lastTrigger.isConnected ? this.lastTrigger : this.btn;
            if (target) target.focus({ preventScroll: true });
            FW.announce(fill(this.s.sizeChosen, { s: b.dataset.sheetSize }));
          },
          { once: true }
        );
        FW.closeDialog(this.sheet);
      });
      this.sheet.addEventListener('close', () => delete this.sheet.dataset.mode);
    }

    /* Auswahl fehlt: Größe im Blatt (mobil) oder Fokus und Rahmen auf der Gruppe (Desktop) */
    guide() {
      const sel = this.selected();
      const group = this.groups.find((g) => sel[+g.dataset.optionIndex] === null);
      const isSize = group && group.dataset.optionKind === 'size';
      if (isSize && this.sheet && !wide.matches) {
        this.sheet.dataset.mode = 'choose';
        FW.openDialog(this.sheet, this.lastTrigger || this.btn);
        return;
      }
      FW.announce(isSize ? this.s.chooseSizeFirst : this.s.chooseOptionFirst);
      if (!group) return;
      const r = group.getBoundingClientRect();
      if (r.top < headerPx() || r.bottom > innerHeight) {
        window.scrollTo({ top: Math.max(0, r.top + window.scrollY - innerHeight / 3), behavior: behavior() });
      }
      group.classList.remove('is-attention');
      void group.offsetWidth;
      group.classList.add('is-attention');
      const first = group.querySelector('[data-option-value]:not(:disabled)');
      if (first) first.focus({ preventScroll: true });
    }

    /* In den Warenkorb */
    async add(trigger) {
      if (this.adding) return;
      this.lastTrigger = trigger;
      if (this.state === 'select') return this.guide();
      if (this.state === 'soldout') {
        const email = this.notify && this.notify.querySelector('input[type="email"]');
        if (email) {
          this.notify.scrollIntoView({ block: 'center', behavior: behavior() });
          email.focus({ preventScroll: true });
        }
        return;
      }
      if (!this.giftCardValid()) return;
      this.adding = true;
      this.clearError();
      this.setLoading(true);
      let ok = false;
      try {
        const fd = new FormData(this.form);
        if (!fd.get('id') && this.variant) fd.set('id', this.variant.id);
        const wrap = this.wrapLine();
        if (!wrap) {
          await FW.cart.add(fd, { trigger, silent: true });
        } else {
          const main = this.lineOf(fd);
          try {
            await FW.cart.add({ items: [main, wrap] }, { trigger, silent: true });
          } catch (err) {
            if (!err || err.status !== 422) throw err;
            /* Ein 422 lässt die ganze Anfrage scheitern. Darum das Teil allein nachlegen und die Meldung zur Verpackung ausgeben. */
            await FW.cart.add({ items: [main] }, { trigger, silent: true });
            this.showError(this.s.wrapFailed);
          }
        }
        ok = true;
      } catch (err) {
        this.showError(err.userMessage || err.message || this.s.notifyError);
      } finally {
        this.adding = false;
        this.setLoading(false, ok);
      }
      if (ok && FW.cartType === 'page' && FW.routes && FW.routes.cart) location.href = FW.routes.cart;
    }

    setLoading(on, ok) {
      [
        [this.btn, '[data-add-label]'],
        [this.stickyBtn, '[data-sticky-label]'],
      ].forEach(([b, selector]) => {
        if (!b) return;
        const l = b.querySelector(selector);
        b.classList.toggle('is-loading', on);
        if (on) {
          b.setAttribute('aria-disabled', 'true');
          if (l) l.textContent = this.s.adding;
        } else {
          b.removeAttribute('aria-disabled');
          if (ok) {
            b.classList.add('is-done');
            if (l) l.textContent = this.s.added;
            setTimeout(() => {
              b.classList.remove('is-done');
              if (l && !this.adding) l.textContent = this.label;
            }, 1800);
          } else if (l) {
            l.textContent = this.label;
          }
          if (this.state === 'soldout') b.setAttribute('aria-disabled', 'true');
        }
      });
    }

    showError(text) {
      if (!this.errorEl) return;
      this.errorEl.textContent = text || '';
      this.errorEl.hidden = !text;
    }
    clearError() {
      this.showError('');
    }

    /* Benachrichtigung: sendet das native Kontaktformular im Hintergrund */
    initNotify() {
      const n = this.notify;
      if (!n) return;
      const success = n.querySelector('[data-notify-success]');
      const failed = n.querySelector('[data-notify-focus]');
      if (success || failed) {
        n.hidden = false;
        requestAnimationFrame(() => {
          (success || failed).focus({ preventScroll: true });
          n.scrollIntoView({ block: 'center' });
        });
      }
      const form = n.querySelector('[data-notify-form]');
      if (!form || !window.fetch || !window.FormData) return;
      form.addEventListener('submit', async (e) => {
        const email = form.querySelector('input[type="email"]');
        if (email && !email.checkValidity()) {
          e.preventDefault();
          email.reportValidity();
          return;
        }
        e.preventDefault();
        const btn = form.querySelector('.notify__btn');
        const fb = form.querySelector('[data-notify-feedback]');
        if (btn) {
          btn.setAttribute('aria-disabled', 'true');
          btn.classList.add('is-loading');
        }
        if (fb) fb.hidden = true;
        try {
          const res = await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (/\/challenge/.test(res.url)) {
            form.submit();
            return;
          }
          const html = await res.text();
          const posted = /contact_posted=true/.test(res.url) || html.indexOf('data-notify-success') > -1;
          if (!posted) throw new Error('notify');
          const p = d.createElement('p');
          p.className = 'form-message form-message--success notify__success';
          p.setAttribute('role', 'status');
          p.tabIndex = -1;
          p.setAttribute('data-notify-success', '');
          p.textContent = fill(this.s.notifySuccess, { v: this.notifyName || this.title });
          form.replaceChildren(p);
          p.focus({ preventScroll: true });
        } catch (err) {
          if (fb) {
            fb.textContent = this.s.notifyError;
            fb.hidden = false;
          }
        } finally {
          if (btn) {
            btn.removeAttribute('aria-disabled');
            btn.classList.remove('is-loading');
          }
        }
      });
    }

    /* Geschenkverpackung: eigenes Produkt als zweite Zeile, Kartentext als Zeilen-Eigenschaft. Das Kästchen startet nie angehakt. */
    initGift() {
      this.wrapEl = this.querySelector('[data-gift-wrap]');
      if (!this.wrapEl) return;
      this.wrapBox = this.wrapEl.querySelector('[data-gift-wrap-box]');
      this.wrapText = this.wrapEl.querySelector('[data-gift-wrap-text]');
      const note = this.wrapEl.querySelector('[data-gift-wrap-note]');
      const count = this.wrapEl.querySelector('[data-gift-wrap-count]');
      if (this.wrapBox) {
        this.wrapBox.checked = false;
        this.wrapBox.addEventListener('change', () => {
          if (note) note.hidden = !this.wrapBox.checked;
        });
      }
      if (this.wrapText && count) {
        const max = +this.wrapText.getAttribute('maxlength') || 200;
        const upd = () => (count.textContent = this.wrapText.value.length + '/' + max);
        this.wrapText.addEventListener('input', upd);
        upd();
      }
    }

    /* Zeile für cart/add.js aus dem Formular: Variante, Menge und Eigenschaften (properties[Name]) */
    lineOf(fd) {
      const line = { id: +fd.get('id'), quantity: +fd.get('quantity') || 1, properties: {} };
      fd.forEach((val, key) => {
        const m = /^properties\[(.+)\]$/.exec(key);
        if (m && val !== '') line.properties[m[1]] = val;
      });
      return line;
    }

    /* Verpackungszeile, wenn das Kästchen gewählt ist: Menge 1, Kartentext sichtbar, _fuer unsichtbar */
    wrapLine() {
      if (!this.wrapBox || !this.wrapBox.checked || !this.variant) return null;
      const line = { id: +this.wrapEl.dataset.wrapVariant, quantity: 1, properties: { _fuer: String(this.variant.id) } };
      const text = this.wrapText ? this.wrapText.value.trim() : '';
      if (text) line.properties[this.wrapEl.dataset.noteKey || 'Kartentext'] = text.slice(0, 200);
      return line;
    }

    /* Gutschein: Empfängerfelder */
    initGiftCard() {
      const box = this.querySelector('[data-gift-card-form]');
      if (!box) return;
      this.giftForm = box;
      const self = box.querySelector('[data-gift-self]');
      const fields = box.querySelector('[data-gift-fields]');
      const offset = box.querySelector('[data-gift-offset]');
      const msg = box.querySelector('[data-gift-message]');
      const count = box.querySelector('[data-gift-count]');
      if (offset) offset.value = new Date().getTimezoneOffset();
      const toggle = () => {
        const off = !!(self && self.checked);
        if (fields) fields.setAttribute('data-off', off ? 'true' : 'false');
        box.querySelectorAll('[data-gift-field]').forEach((f) => (f.disabled = off));
      };
      if (self) self.addEventListener('change', toggle);
      if (msg && count) {
        const max = +msg.getAttribute('maxlength') || 200;
        const upd = () => (count.textContent = msg.value.length + '/' + max);
        msg.addEventListener('input', upd);
        upd();
      }
      toggle();
    }

    giftCardValid() {
      if (!this.giftForm) return true;
      const req = this.giftForm.querySelector('[data-gift-required]');
      if (req && !req.disabled && !req.checkValidity()) {
        req.reportValidity();
        req.focus();
        return false;
      }
      return true;
    }

    /* Sticky-Leiste: erscheint erst, wenn der Hauptknopf nach oben aus dem Bild gescrollt ist (nicht schon, solange er noch
       unterhalb des sichtbaren Bereichs liegt) und solange der Footer nicht im Bild steht. */
    initSticky() {
      if (!this.sticky || !this.btn || !('IntersectionObserver' in window)) return;
      let btnGone = false;
      let footVisible = false;
      const show = () => {
        const on = btnGone && !footVisible;
        this.sticky.classList.toggle('is-visible', on);
        if (on) {
          this.sticky.removeAttribute('inert');
          this.sticky.removeAttribute('aria-hidden');
          if (this.stickyBtn) this.stickyBtn.removeAttribute('tabindex');
        } else {
          this.sticky.setAttribute('inert', '');
          this.sticky.setAttribute('aria-hidden', 'true');
          if (this.stickyBtn) this.stickyBtn.tabIndex = -1;
        }
      };
      const io = new IntersectionObserver((en) => {
        const e = en[en.length - 1];
        btnGone = !e.isIntersecting && e.boundingClientRect.top < 0;
        show();
      });
      io.observe(this.btn);
      /* Der Beobachter meldet nur Wechsel. Springt die Seite am Knopf vorbei (Anker, wiederhergestellte Scrollposition), gibt es
         keinen Wechsel und keine Meldung. Darum prüft ein Scroll-Handler den Knopf zusätzlich. */
      let queued = false;
      const measure = () => {
        queued = false;
        const r = this.btn.getBoundingClientRect();
        const gone = r.height > 0 && r.bottom < 0;
        if (gone !== btnGone) {
          btnGone = gone;
          show();
        }
      };
      const onScroll = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(measure);
      };
      addEventListener('scroll', onScroll, { passive: true });
      measure();
      const foot = d.querySelector('footer');
      let io2;
      if (foot) {
        io2 = new IntersectionObserver((en) => {
          footVisible = en[en.length - 1].isIntersecting;
          show();
        });
        io2.observe(foot);
      }
      this.cleanup = (this.cleanup || []).concat([() => io.disconnect(), () => io2 && io2.disconnect(), () => removeEventListener('scroll', onScroll)]);
    }

    /* Infospalte klebt nur, wenn sie ins Fenster passt */
    initStickyInfo() {
      if (!this.layout || this.dataset.stickyInfo !== 'true' || !('ResizeObserver' in window)) return;
      const check = () => this.layout.classList.toggle('is-tall', this.offsetHeight > innerHeight - headerPx() - 32);
      const ro = new ResizeObserver(check);
      ro.observe(this);
      addEventListener('resize', check, { passive: true });
      check();
      this.cleanup = (this.cleanup || []).concat([() => ro.disconnect(), () => removeEventListener('resize', check)]);
    }
  }
  if (!customElements.get('fw-product-info')) customElements.define('fw-product-info', FwProductInfo);
})();
